import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchCodeSource, type CodeRef } from "../../api/code";

interface CodeSourcePanelProps {
  projectId: string;
  codeRef: CodeRef;
  isLight: boolean;
}

function shortRef(ref: string | undefined): string {
  return ref ? ref.slice(0, 12) : "unknown";
}

function repoLabel(codeRef: CodeRef): string {
  const owner = codeRef.repository?.owner ?? "github";
  const name = codeRef.repository?.name ?? "repo";
  return `${owner}/${name}`;
}

export function CodeSourcePanel({ projectId, codeRef, isLight }: CodeSourcePanelProps) {
  const query = useQuery({
    queryKey: [
      "code-source",
      projectId,
      codeRef.repository?.owner,
      codeRef.repository?.name,
      codeRef.commit,
      codeRef.relative_path,
    ],
    queryFn: () => fetchCodeSource(projectId, codeRef),
    enabled: Boolean(projectId && codeRef.repository?.owner && codeRef.repository?.name && codeRef.relative_path),
    staleTime: 5 * 60 * 1000,
  });

  const lines = useMemo(() => (query.data?.content ?? "").split("\n"), [query.data?.content]);
  const highlightedLine = query.data?.line_number ?? codeRef.line_number ?? null;
  const githubUrl = query.data?.url ?? codeRef.url ?? codeRef.commit_url ?? codeRef.repository?.url;
  const path = query.data?.path ?? codeRef.relative_path ?? codeRef.repository?.name ?? "source";
  const commit = query.data?.ref ?? codeRef.commit;

  return (
    <section
      aria-label="Generating script"
      style={{
        borderTop: `1px solid ${isLight ? "#e1e5e2" : "#2a302c"}`,
        background: isLight ? "#fafafa" : "#131714",
        color: isLight ? "#111813" : "#f0f2f0",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "9px 12px",
          borderBottom: `1px solid ${isLight ? "#e6e9e7" : "#242a26"}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0, textTransform: "uppercase", opacity: 0.55 }}>
            Generating script
          </div>
          <div
            style={{
              marginTop: 2,
              fontSize: 12,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={`${repoLabel(codeRef)}:${path}`}
          >
            {repoLabel(codeRef)} / {path}
          </div>
          <div style={{ marginTop: 2, fontSize: 10, opacity: 0.5, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span>{shortRef(commit)}</span>
            {highlightedLine ? <span>line {highlightedLine}</span> : null}
            {codeRef.dirty ? <span style={{ color: isLight ? "#b45309" : "#fbbf24" }}>dirty {codeRef.dirty_status ?? ""}</span> : null}
          </div>
        </div>
        {githubUrl ? (
          <a
            href={githubUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              border: `1px solid ${isLight ? "#d0d5d2" : "#363d39"}`,
              borderRadius: 5,
              padding: "5px 9px",
              color: isLight ? "#111813" : "#d1d9d3",
              textDecoration: "none",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Open GitHub
          </a>
        ) : null}
      </div>

      <div
        style={{
          maxHeight: 280,
          overflow: "auto",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: 11,
          lineHeight: 1.55,
          background: isLight ? "#ffffff" : "#0b0f0c",
        }}
      >
        {query.isLoading ? (
          <div style={{ padding: 12, opacity: 0.55 }}>Loading source...</div>
        ) : query.isError ? (
          <div style={{ padding: 12, color: isLight ? "#9f1239" : "#fda4af" }}>
            Source unavailable for this repository reference.
          </div>
        ) : (
          <div style={{ margin: 0, padding: "8px 0", minWidth: "max-content" }}>
            {lines.map((line, index) => {
              const lineNo = index + 1;
              const highlighted = highlightedLine === lineNo;
              return (
                <div
                  key={lineNo}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "52px minmax(0, 1fr)",
                    background: highlighted
                      ? (isLight ? "rgba(245,158,11,0.16)" : "rgba(251,191,36,0.16)")
                      : "transparent",
                  }}
                >
                  <span
                    style={{
                      textAlign: "right",
                      paddingRight: 10,
                      color: highlighted ? (isLight ? "#92400e" : "#fbbf24") : (isLight ? "#9ca3af" : "#4b5563"),
                      userSelect: "none",
                    }}
                  >
                    {lineNo}
                  </span>
                  <code style={{ whiteSpace: "pre", paddingRight: 16 }}>{line || " "}</code>
                </div>
              );
            })}
            {query.data?.truncated ? (
              <div style={{ padding: "8px 12px", opacity: 0.55 }}>File truncated for preview.</div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}

export default CodeSourcePanel;
